"use client";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  SpecialAccessResponse,
  consumptionStateCategoryValues,
  ConsumptionStateCategoryEnum,
  SpecialAccessDataWithContent,
  SpecialAccessWebOfferPageResponse,
} from "@/screens/specialAccess/types/specialAccess.types";
import { apiSdkInstance } from "@/apiSdk/stageApiSdk.instance";
import {
  CookiesEnum,
  cookiesValues,
  LangEnum,
  ContentTypeEnum,
  GamificationPlanIdEnum,
} from "@/constants/constants";
import {
  getClientSideCookieValue,
  getCurrentLang,
  getDialect,
  generateWatchPageURL,
  getContentTypeUrlValue,
  getContentDetail,
} from "@/utils/common.helpers";
import { useUserContext } from "./UserProvider";
import {
  setCookieViaAction,
  setMultipleCookiesViaAction,
} from "@/actions/user.actions";
import { usePathname } from "next/navigation";

interface SpecialAccessContextType {
  // Current special access state data with content appended
  currentSpecialAccessData: SpecialAccessDataWithContent | null;
  // Total consumption states (states.length - 3)
  totalConsumptionStates: number | null;
  // Function to set special access data (accepts full response)
  setSpecialAccessData: (data: SpecialAccessResponse | null) => void;
  // Offer page data
  offerPageData: SpecialAccessWebOfferPageResponse | null;
  // Function to set offer page data
  setOfferPageData: (data: SpecialAccessWebOfferPageResponse | null) => void;
}

const SpecialAccessContext = createContext<
  SpecialAccessContextType | undefined
>(undefined);

export const SpecialAccessProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [responseData, setResponseData] =
    useState<SpecialAccessResponse | null>(null);
  const [offerPageData, setOfferPageData] =
    useState<SpecialAccessWebOfferPageResponse | null>(null);
  const { isLoggedIn, hasActiveAccess, isTrialEligibleUser } = useUserContext();
  const pathname = usePathname();
  const previousLangRef = useRef<string | null>(null);
  const hasClearedForSubscribedUserRef = useRef<boolean>(false);

  // Clear special access data when subscribed user logs in
  useEffect(() => {
    if (
      responseData &&
      isLoggedIn &&
      (hasActiveAccess || !isTrialEligibleUser) &&
      !hasClearedForSubscribedUserRef.current
    ) {
      setResponseData(null);
      hasClearedForSubscribedUserRef.current = true;
    }
    // Reset the flag when user logs out or loses access
    if (!isLoggedIn || !hasActiveAccess) {
      hasClearedForSubscribedUserRef.current = false;
    }
  }, [isLoggedIn, hasActiveAccess, isTrialEligibleUser]);

  // Initial API call to get user's special access state if they're already in special access
  useEffect(() => {
    const fetchInitialSpecialAccessState = async () => {
      // Don't fetch if user has active access (they shouldn't have special access)
      if (hasActiveAccess) {
        return;
      }

      // Only fetch if user is logged in and we haven't initialized yet
      if (!isLoggedIn || responseData) {
        return;
      }

      // Check if paywall feature is special-access (using trialPlanId cookie)
      const trialPlanId = getClientSideCookieValue(
        cookiesValues[CookiesEnum.TrialPlanId],
      );
      const isSpecialAccessEnabled =
        trialPlanId === GamificationPlanIdEnum.SPECIAL_ACCESS;

      // Only make API call if special access is enabled
      if (!isSpecialAccessEnabled) {
        return;
      }

      try {
        const lang = (await getCurrentLang()) as LangEnum;
        const dialect = (await getDialect()) || "";

        const response =
          await apiSdkInstance.specialAccess.getSpecialAccessUserState({
            lang,
            dialect,
          });

        if (response) {
          setResponseData(response);

          // Set special access related cookies after successful API call
          await setMultipleCookiesViaAction([
            {
              key: cookiesValues[CookiesEnum.SpecialAccessContentIds],
              value: response.content.all_ids.join(","),
            },
            {
              key: cookiesValues[
                CookiesEnum.SpecialAccessConsumptionStateCategory
              ],
              value: response.current_state_category,
            },
            {
              key: cookiesValues[
                CookiesEnum.SpecialAccessConsumptionStateValue
              ],
              value: response.current_state_value.toString(),
            },
          ]);
        }
      } catch (error) {
        // Silently fail - user might not be in special access or API might fail
        console.warn("Failed to fetch initial special access state:", error);
      }
    };

    fetchInitialSpecialAccessState();
  }, [isLoggedIn, hasActiveAccess]);

  // Calculate total consumption states (states.length - 3)
  const totalConsumptionStates = useMemo(() => {
    if (!responseData || !responseData.states) {
      return null;
    }
    return responseData.states.length - 3;
  }, [responseData]);

  // Calculate the current state index and return state with content appended
  const currentSpecialAccessData = useMemo(() => {
    if (!responseData) {
      return null;
    }

    const categoryEnum =
      responseData.current_state_category as ConsumptionStateCategoryEnum;

    // Get the numeric value for current_state_category
    const categoryValue = consumptionStateCategoryValues[categoryEnum];

    // Add current_state_value to get the final index (0-5)
    const stateIndex = categoryValue + responseData.current_state_value;

    // Get the state at that index
    const currentState = responseData.states[stateIndex];

    if (!currentState) {
      return null;
    }

    // Return the state with content appended
    return {
      ...currentState,
      content: responseData.content,
      current_state_category: responseData.current_state_category,
      current_state_slot: responseData.current_state_slot,
      current_state_value: responseData.current_state_value,
    };
  }, [responseData]);

  const setSpecialAccessData = useCallback(
    (data: SpecialAccessResponse | null) => {
      setResponseData(data);
    },
    [],
  );

  // Update watch page URL cookie when language changes
  useEffect(() => {
    const updateWatchPageUrlForLanguageChange = async () => {
      // Need responseData to have content info
      if (!responseData) {
        return;
      }

      // Get current language using getCurrentLang helper
      const currentLang = (await getCurrentLang()) as LangEnum | undefined;

      if (!currentLang) {
        return;
      }

      // Check if language has changed
      if (previousLangRef.current === currentLang) {
        return;
      }

      // Update previous language ref
      previousLangRef.current = currentLang;

      try {
        // Get dialect from cookie (stored in SpecialAccess.tsx)
        const dialect =
          getClientSideCookieValue(
            cookiesValues[CookiesEnum.SpecialAccessDialect],
          ) || "";

        // Get content detail for the new language to get the correct slug
        const contentType = getContentTypeUrlValue(responseData.content.type);
        const contentDetail = await getContentDetail({
          titleId: responseData.content.id,
          contentType: contentType,
          lang: currentLang,
          dialect: dialect,
          addToken: false,
        });

        // Determine if it's a movie (movies don't have episode numbers)
        const isMovie = responseData.content.type === ContentTypeEnum.MOVIE;
        const episodeNo = isMovie ? null : 1;

        // Generate new watch page URL with new language and slug
        const newWatchPageUrl = generateWatchPageURL(
          currentLang,
          dialect,
          contentType,
          contentDetail.slug,
          episodeNo,
          false, // Don't include current query params
          {}, // No additional query params
        );

        // Update the cookie
        await setCookieViaAction({
          key: cookiesValues[CookiesEnum.SpecialAccessWatchPageUrl],
          value: newWatchPageUrl,
        });
      } catch (error) {
        // Silently fail - might not be able to update URL
        console.warn(
          "Failed to update watch page URL for language change:",
          error,
        );
      }
    };

    updateWatchPageUrlForLanguageChange();
  }, [responseData, pathname]);

  return (
    <SpecialAccessContext.Provider
      value={{
        currentSpecialAccessData,
        totalConsumptionStates,
        setSpecialAccessData,
        offerPageData,
        setOfferPageData,
      }}
    >
      {children}
    </SpecialAccessContext.Provider>
  );
};

export const useSpecialAccessContext = () => {
  const context = useContext(SpecialAccessContext);
  if (!context) {
    throw new Error(
      "useSpecialAccessContext must be used within a SpecialAccessProvider",
    );
  }
  return context;
};
